import { Button, StatusIndicator, Popover } from "@amzn/awsui-components-react";
import { getString } from "common/ui_string_labels/ui-strings-utils";

const getStringPath = "common.component.CopyText"

/**
 * Props for the CopyText component
 * 
 * @interface
 * @property {string} [textContent] - The text to display in the component
 * @property {string} copyContent - The content to copy to clipboard
 * @property {boolean} [isHtml] - Whether the content contains HTML that should be parsed
 */
interface CopyTextProps {
    textContent?: string;
    copyContent: string;
    isHtml?: boolean;
}

/**
 * Copies text to the clipboard using the Clipboard API
 *
 * @param {string} text - The text to copy to clipboard
 * @return {Promise<void>} - Promise that resolves when text is copied
 */
function copyToClipboard(text: string) {
    return navigator.clipboard.writeText(text);
}

/**
 * Extracts plain text from HTML content or returns the value as-is
 *
 * @param {any} value - The content value to process
 * @param {boolean | undefined} isHtmlContent - Whether to parse as HTML
 * @return {string} - The processed text content
 */
export function getContentText(value: any, isHtmlContent: boolean | undefined) {
    if (value && isHtmlContent) {
        var divContainer = document.createElement("div");
        divContainer.innerHTML = value;
        return divContainer.textContent || divContainer.innerText || "";
    }

    return value ?? "";
}

const CopyText = ({ textContent, copyContent, isHtml }: CopyTextProps) => {
    const contentText = getContentText(textContent, isHtml);
    const contentToCopy = getContentText(copyContent, isHtml);

    return (
        <Popover
            size="small"
            position="top"
            dismissButton={false}
            triggerType="custom"
            content={<StatusIndicator type="success">{contentToCopy} {getString(`${getStringPath}.copiedSuccessfully`)}</StatusIndicator>}
        >
            {contentText}
            <Button variant="inline-icon" iconName="copy" onClick={() => copyToClipboard(contentToCopy)} />
        </Popover>
    );
};

export default CopyText;
